import type { AxiosRequestConfig } from 'axios'
import { useState, useCallback } from 'react'
import { useFileUploadMutation } from 'v2/shared/services/queries/file-upload'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'

export const useFileUpload = (
  onUploaded: (file: RFileUploadResponse) => void,
  config?: AxiosRequestConfig,
) => {
  const [uploadedFiles, setUploadedFiles] = useState<RFileUploadResponse[]>([])
  const uploadMutation = useFileUploadMutation(config)

  const uploadFile = useCallback(
    async (file: File) => {
      const fileData = new FormData()
      fileData.append('file', file)

      try {
        const result = await uploadMutation.mutateAsync(fileData)
        setUploadedFiles((prev) => [...prev, result])
        onUploaded(result)
        return result
      } catch (error) {
        logger.error('File upload failed:', error)
        errorToast({
          title: `Upload Failed: ${file.name}`,
          description: `${error}. Please try again.`,
        })
        return null
      }
    },
    [uploadMutation, onUploaded],
  )

  const removeFile = useCallback((id: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.file_id !== id))
  }, [])

  return {
    uploadFile,
    uploadedFiles,
    removeFile,
    isUploading: uploadMutation.isPending, // Changed from isLoading to isPending
  }
}
